import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  getAmbassadorListBasedOnCriteria,
  saveClientAmbasadorList,
} from "../../../../apis/clients.api";
import SuggestedAmbassadorCard, {
  getAmbassadorId,
} from "./suggested-ambassador-card";

function buildClientUpdatePayload(clientDetails, targetAmbassadorIds: string[]) {
  const {
    firstName,
    lastName,
    email,
    title,
    company,
    no_of_emp,
    funding,
    revenues,
    target_industries,
    target_no_of_emp,
    target_account,
    linkedIn,
    timezone,
    viewAmbassadorThreshold,
    meetingThreshold,
    viewNextBatchAmbassadorThreshold,
    licenseForMeeting,
  } = clientDetails;

  return {
    firstName,
    lastName,
    email,
    title,
    company,
    no_of_emp,
    funding,
    revenues,
    target_industries,
    target_no_of_emp,
    target_account,
    linkedIn,
    timezone,
    viewAmbassadorThreshold,
    meetingThreshold,
    viewNextBatchAmbassadorThreshold,
    licenseForMeeting,
    target_ambassadors: targetAmbassadorIds,
  };
}

export default function ClientSuggestedAmbassadors() {
  const dispatch = useDispatch();
  const { id: clientId }: any = useParams();
  const clientDetails = useSelector(
    (state: any) => state.clients.selectedClient
  );
  const [suggested, setSuggested] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    const ids = clientDetails?.target_ambassadors?.map(String) || [];
    setSelectedIds(ids);
  }, [clientDetails?.target_ambassadors]);

  const loadSuggested = useCallback(async () => {
    setLoading(true);
    const response: any = await dispatch(
      getAmbassadorListBasedOnCriteria(clientId)
    );
    if (response?.success) {
      setSuggested(response.data?.suggested || []);
    } else {
      setSuggested([]);
    }
    setLoading(false);
  }, [clientId, dispatch]);

  useEffect(() => {
    if (clientId) {
      loadSuggested();
    }
  }, [clientId, loadSuggested]);

  const handleToggle = async (ambassador, checked: boolean) => {
    const ambassadorId = getAmbassadorId(ambassador);
    if (!ambassadorId || !clientDetails) return;

    const nextIds = checked
      ? selectedIds.includes(String(ambassadorId))
        ? selectedIds
        : [...selectedIds, String(ambassadorId)]
      : selectedIds.filter((id) => id !== String(ambassadorId));

    setSelectedIds(nextIds);
    setSaving(true);

    const response: any = await dispatch(
      saveClientAmbasadorList(
        clientId,
        buildClientUpdatePayload(clientDetails, nextIds)
      )
    );

    if (!response?.success) {
      setSelectedIds(
        clientDetails?.target_ambassadors?.map(String) || []
      );
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} sx={{ color: "#070639" }} />
      </Box>
    );
  }

  if (!suggested.length) {
    return (
      <Box py={3} textAlign="center">
        <Typography color="#6B7280" fontSize={14}>
          No matching ambassadors available yet. Suggestions appear when the client
          has value props and ambassadors have provided initiatives.
        </Typography>
      </Box>
    );
  }

  const count = suggested.length;
  const autoCols = (maxPerRow: number) => {
    const perRow = Math.min(maxPerRow, count);
    return Math.floor(12 / perRow);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {suggested.map((ambassador) => {
          const ambassadorId = getAmbassadorId(ambassador);
          return (
            <Grid
              item
              xs={12}
              md={4}
              lg={autoCols(3)}
              xl={autoCols(4)}
              key={ambassadorId}
            >
              <SuggestedAmbassadorCard
                ambassador={ambassador}
                selected={selectedIds.includes(String(ambassadorId))}
                disabled={saving}
                onToggle={handleToggle}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
